import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Card, CardHeader, Btn, Input, Table, fmt, Badge, PageHeader } from '../../components/ui';
import { Plus, IndianRupee } from 'lucide-react';

const QUICK_AMOUNTS = [1500, 2000, 2500, 3000];
const INIT = { plan_name: '', amount: '', description: '' };

export default function BrokerageMasterPage() {
  const [brokers, setBrokers] = useState([]);
  const [form, setForm] = useState(INIT);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => { load(); }, []);
  const load = () => api.get('/masters/brokerages').then(r => setBrokers(r.data));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.plan_name || !form.amount) { toast.error('Plan name and amount required'); return; }
    setLoading(true);
    try {
      if (editId) { await api.put(`/masters/brokerages/${editId}`, { ...form, is_active: true }); toast.success('Updated!'); }
      else { await api.post('/masters/brokerages', form); toast.success('Plan added!'); }
      setForm(INIT); setShowForm(false); setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  const startEdit = (b) => {
    setForm({ plan_name: b.plan_name, amount: b.amount, description: b.description || '' });
    setEditId(b.id); setShowForm(true);
  };

  const del = async (id) => {
    if (!window.confirm('Deactivate this plan?')) return;
    await api.delete(`/masters/brokerages/${id}`).catch(() => {});
    load();
  };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <PageHeader
        title="Brokerage Master"
        subtitle="Configure fixed brokerage plans (₹ per trade)"
        action={<Btn onClick={() => { setShowForm(v => !v); setEditId(null); setForm(INIT); }}><Plus size={15} /> Add Plan</Btn>}
      />

      {showForm && (
        <Card style={{ marginBottom: 22 }}>
          <CardHeader title={editId ? 'Edit Plan' : 'New Brokerage Plan'} subtitle="Fixed rupee amount charged per transaction" />
          <div style={{ padding: 22 }}>
            {/* Quick presets */}
            {!editId && (
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>QUICK PRESETS</div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {QUICK_AMOUNTS.map(amt => (
                    <button key={amt} type="button"
                      onClick={() => setForm({ plan_name: `₹${amt.toLocaleString()} Plan`, amount: amt, description: `Fixed brokerage ₹${amt.toLocaleString()} per trade` })}
                      style={{
                        padding: '10px 20px', borderRadius: 9, border: '2px solid',
                        borderColor: parseFloat(form.amount) === amt ? '#2563eb' : '#e2e8f0',
                        background: parseFloat(form.amount) === amt ? '#eff6ff' : '#f8fafc',
                        color: parseFloat(form.amount) === amt ? '#1d4ed8' : '#374151',
                        fontWeight: 700, cursor: 'pointer', fontSize: 14, transition: 'all 0.15s'
                      }}>
                      ₹{amt.toLocaleString()}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <form onSubmit={submit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 14 }}>
                <Input label="Plan Name *" value={form.plan_name} onChange={set('plan_name')} placeholder="e.g. Standard Plan" />
                <Input label="Amount (₹) *" type="number" min="1" step="0.01" value={form.amount} onChange={set('amount')} placeholder="e.g. 2000" />
                <Input label="Description" value={form.description} onChange={set('description')} placeholder="Optional note" />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <Btn type="submit" disabled={loading}>{loading ? 'Saving…' : editId ? 'Update Plan' : 'Add Plan'}</Btn>
                <Btn variant="ghost" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Btn>
              </div>
            </form>
          </div>
        </Card>
      )}

      <Card>
        <CardHeader title="Brokerage Plans" subtitle={`${brokers.length} active plans`} />
        <Table
          headers={['Plan Name', 'Amount (₹)', 'Description', 'Actions']}
          rows={brokers.map(b => [
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IndianRupee size={15} color="#2563eb" />
              </div>
              <strong>{b.plan_name}</strong>
            </div>,
            <span style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{fmt(b.amount)}</span>,
            <span style={{ color: '#64748b', fontSize: 12 }}>{b.description || '—'}</span>,
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn size="sm" variant="ghost" onClick={() => startEdit(b)}>Edit</Btn>
              <Btn size="sm" variant="danger" onClick={() => del(b.id)}>Delete</Btn>
            </div>
          ])}
          empty="No brokerage plans. Add your first plan above."
        />
      </Card>
    </div>
  );
}
